import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

const ROLE_HIERARCHY: Record<string, number> = {
  'platform-admin': 100,
  'program-admin': 80,
  'instructor': 60,
  'learner': 10
};

const routes: RouteRecordRaw[] = [
  // Marketing pages (public - accessible to all)
  {
    path: '/',
    name: 'home',
    component: () => import('@/components/marketing/LandingPage.vue'),
    meta: { public: true }
  },
  {
    path: '/for-organizations',
    name: 'for-organizations',
    component: () => import('@/components/marketing/ForOrganizationsPage.vue'),
    meta: { public: true }
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/components/marketing/TermsOfService.vue'),
    meta: { public: true }
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/components/marketing/PrivacyPolicy.vue'),
    meta: { public: true }
  },
  {
    path: '/for-developers',
    name: 'for-developers',
    component: () => import('@/components/marketing/ForDevelopersPage.vue'),
    meta: { public: true }
  },
  {
    path: '/for-leaders',
    name: 'for-leaders',
    component: () => import('@/components/marketing/ForLeadersPage.vue'),
    meta: { public: true }
  },
  {
    path: '/for-specialists',
    name: 'for-specialists',
    component: () => import('@/components/marketing/ForSpecialistsPage.vue'),
    meta: { public: true }
  },
  // Auth pages (guest only - redirect if logged in)
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/pages/RegisterPage.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/pages/ForgotPasswordPage.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/pages/ResetPasswordPage.vue'),
    meta: { requiresGuest: true }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/pages/DashboardPage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/programs/:slug',
    name: 'program',
    component: () => import('@/pages/CoursePage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/programs/:slug/module/:moduleId',
    name: 'module',
    component: () => import('@/pages/ModulePage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/programs/:slug/exercise/:exerciseId',
    name: 'exercise',
    component: () => import('@/pages/ExercisePage.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/instructor/progress/:slug',
    name: 'instructor-progress',
    component: () => import('@/pages/instructor/ProgressView.vue'),
    meta: { requiresAuth: true, requiresRole: 'instructor' }
  },
  {
    path: '/instructor/pending/:slug',
    name: 'instructor-pending',
    component: () => import('@/pages/instructor/PendingEnrollments.vue'),
    meta: { requiresAuth: true, requiresRole: 'instructor' }
  },
  {
    path: '/settings',
    name: 'settings',
    component: () => import('@/pages/SettingsPage.vue'),
    meta: { requiresAuth: true }
  },
  // Admin routes
  {
    path: '/admin',
    component: () => import('@/pages/admin/AdminLayout.vue'),
    meta: { requiresAuth: true, requiresRole: 'platform-admin' },
    children: [
      {
        path: '',
        name: 'admin-dashboard',
        component: () => import('@/pages/admin/DashboardHome.vue')
      },
      {
        path: 'users',
        name: 'admin-users',
        component: () => import('@/pages/admin/UserManagement.vue')
      },
      {
        path: 'users/:id',
        name: 'admin-user-detail',
        component: () => import('@/pages/admin/UserDetail.vue')
      },
      {
        path: 'programs',
        name: 'admin-programs',
        component: () => import('@/pages/admin/ProgramManagement.vue')
      },
      {
        path: 'programs/:id',
        name: 'admin-program-detail',
        component: () => import('@/pages/admin/ProgramDetail.vue')
      },
      {
        path: 'enrollments',
        name: 'admin-enrollments',
        component: () => import('@/pages/admin/EnrollmentList.vue')
      },
      {
        path: 'enrollments/:id',
        name: 'admin-enrollment-detail',
        component: () => import('@/pages/admin/EnrollmentDetail.vue')
      },
      {
        path: 'audit-logs',
        name: 'admin-audit-logs',
        component: () => import('@/pages/admin/AuditLogs.vue')
      },
      {
        path: 'email-logs',
        name: 'admin-email-logs',
        component: () => import('@/pages/admin/EmailLogs.vue')
      }
    ]
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    // If browser back/forward, restore saved position
    if (savedPosition) {
      return savedPosition;
    }
    // Otherwise scroll to top
    return { top: 0 };
  }
});

// Navigation guards
router.beforeEach(async (to, _from, next) => {
  const isPublic = to.matched.some(record => record.meta.public);
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
  const requiresGuest = to.matched.some(record => record.meta.requiresGuest);
  const requiredRole = to.meta.requiresRole as string | undefined;

  // Public routes are accessible to everyone - no auth check needed
  if (isPublic) {
    return next();
  }

  // Check if user is authenticated
  let isAuthenticated = false;
  let userRoles: Array<{ name: string; level: number }> = [];

  try {
    const response = await fetch('/api/auth/me', { credentials: 'include' });
    if (response.ok) {
      isAuthenticated = true;
      const data = await response.json();
      userRoles = data.roles || [];
    }
  } catch {
    // Not authenticated
  }

  // Redirect unauthenticated users trying to access protected routes
  if (requiresAuth && !isAuthenticated) {
    return next({ name: 'login', query: { redirect: to.fullPath } });
  }

  // Redirect authenticated users away from guest-only routes
  if (requiresGuest && isAuthenticated) {
    return next({ name: 'dashboard' });
  }

  // Check role requirements
  if (requiredRole && isAuthenticated) {
    const userMaxLevel = Math.max(0, ...userRoles.map(r => r.level));
    const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;

    if (userMaxLevel < requiredLevel) {
      return next({ name: 'dashboard' });
    }
  }

  next();
});

export default router;
